import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CategoryFieldMapDataModel, CategoryFieldValuesMapDataModel, CategorySearchDataModel, ExportedCategoryListDataModel } from '@app/category-taxonomy/models';
import { BaseCategoriesRequest } from '@app/category-taxonomy/services/requests/base-categories.request';
import { CategoryFieldValuesRequest } from '@app/category-taxonomy/services/requests/category-field-values.request';
import { CategoryPathsRequest } from '@app/category-taxonomy/services/requests/category-paths.request';
import { GetCategoriesRequest } from '@app/category-taxonomy/services/requests/get-categories.request';
import { UniqueAttributesRequest } from '@app/category-taxonomy/services/requests/unique-attributes.request';
import { CategoryPathsResponse } from '@app/category-taxonomy/services/responses/category-paths.response';
import { ChannelsWithRegionsResponse } from '@app/category-taxonomy/services/responses/channels-with-regions.response';
import { GetCategoryAuthResponse } from '@app/category-taxonomy/services/responses/get-category-auth.response';
import { UniqueAttributeField } from '@app/category-taxonomy/services/responses/unique-attributes.response';
import { Observable, map } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CategoryTaxonomyDataService {

    constructor(
        private readonly http: HttpClient
    ) {}

    getCategories(databaseId: string, params: GetCategoriesRequest, page: number = 1, pageSize: number = 50): Observable<CategorySearchDataModel> {
        return this.http.get<CategorySearchDataModel>(`/api.php/dbs/${databaseId}/category_taxonomy/${page}/${pageSize}`, {
            params: params
        }).pipe(map((categories) => {
            // ensure data integrity
            return {
                ...categories,
                current_page: parseInt(`${categories.current_page}`, 10),
                page_size: parseInt(`${categories.page_size}`, 10),
                total_pages: Math.ceil(categories.total_pages)
            };
        }));
    }

    getCategoryAuth(databaseId: string, client: string): Observable<GetCategoryAuthResponse> {
        const url = `/api.php/dbs/${databaseId}/category_taxonomy_required_attributes`;
        const params = {
            client
        };

        return this.http.get<GetCategoryAuthResponse>(url, { params });
    }

    getCategoryFields(databaseId: string, categoryId: string, params: BaseCategoriesRequest): Observable<CategoryFieldMapDataModel> {
        const url = `/api.php/dbs/${databaseId}/category_taxonomy/${categoryId}`;
        return this.http.get<CategoryFieldMapDataModel>(url, { params });
    }

    getCategoryFieldValues(databaseId: string, categoryId: string, params: CategoryFieldValuesRequest): Observable<CategoryFieldValuesMapDataModel> {
        const url = `/api.php/dbs/${databaseId}/category_taxonomy_values_for_attribute/${categoryId}`;
        return this.http.get<CategoryFieldValuesMapDataModel>(url, { params });
    }

    getCategoryList(databaseId: string, params: BaseCategoriesRequest): Observable<ExportedCategoryListDataModel> {
        return this.http.get<ExportedCategoryListDataModel>(`/api.php/dbs/${databaseId}/category_list`, { params });
    }

    getCategoryPaths(databaseId: string, params: CategoryPathsRequest): Observable<CategoryPathsResponse> {
        return this.http.get<CategoryPathsResponse>(`/api.php/dbs/${databaseId}/category_paths/`, { params });
    }

    getChannelsWithRegions(databaseId: string, includeTaxonomyService: boolean = false): Observable<ChannelsWithRegionsResponse> {
        const params: Record<string, string> = includeTaxonomyService ? { include_taxonomy_service: 'true' } : {};
        return this.http.get<ChannelsWithRegionsResponse>(`/api.php/dbs/${databaseId}/channels_with_regions`, { params });
    }

    getUniqueAttributes(databaseId: string, params: UniqueAttributesRequest): Observable<UniqueAttributeField[]> {
        return this.http.post<UniqueAttributeField[]>(`/api.php/dbs/${databaseId}/unique_taxonomy_attributes`, params);
    }
}
